/**
 * Parts-per notation and the concentrations it is converted to.
 *
 * Three kinds of unit are in play, and a conversion between kinds needs one extra fact:
 *
 * - **mass fraction** (ppm, ppb, ppt, %, mg/kg): grams of solute per gram of solution;
 * - **mass concentration** (mg/L, µg/L, g/L): grams of solute per litre of solution;
 * - **amount concentration** (mol/L, mM, µM): moles of solute per litre of solution.
 *
 * Fraction ↔ concentration needs the solution's **density** (a litre of solution weighs
 * 1000 × ρ grams, ρ in g/mL); concentration ↔ molarity needs the **molar mass**.
 *
 * "ppm" here means a mass fraction of 10⁻⁶, so 1 ppm = 1 mg/kg. For a dilute aqueous
 * solution with ρ ≈ 1.000 g/mL that is also 1 mg/L, which is why the two are used
 * interchangeably in water analysis; at any other density they differ, and this module
 * keeps them apart. ppb is 10⁻⁹ and ppt is 10⁻¹² (short scale: a billion is 10⁹).
 * Gas-phase ppmv is a volume (mole) fraction, a different quantity, and is not handled.
 */

export type PartsKind = "fraction" | "massConc" | "molar";

export interface PartsUnit {
  id: string;
  symbol: string;
  kind: PartsKind;
  /** Fraction: dimensionless. massConc: g/L. molar: mol/L. */
  factor: number;
}

export const PARTS_UNITS: readonly PartsUnit[] = [
  { id: "ppm", symbol: "ppm", kind: "fraction", factor: 1e-6 },
  { id: "ppb", symbol: "ppb", kind: "fraction", factor: 1e-9 },
  { id: "ppt", symbol: "ppt", kind: "fraction", factor: 1e-12 },
  { id: "pct", symbol: "%", kind: "fraction", factor: 1e-2 },
  { id: "mgkg", symbol: "mg/kg", kind: "fraction", factor: 1e-6 },
  { id: "ugkg", symbol: "µg/kg", kind: "fraction", factor: 1e-9 },
  { id: "gL", symbol: "g/L", kind: "massConc", factor: 1 },
  { id: "mgL", symbol: "mg/L", kind: "massConc", factor: 1e-3 },
  { id: "ugL", symbol: "µg/L", kind: "massConc", factor: 1e-6 },
  { id: "ngL", symbol: "ng/L", kind: "massConc", factor: 1e-9 },
  { id: "M", symbol: "mol/L", kind: "molar", factor: 1 },
  { id: "mM", symbol: "mmol/L", kind: "molar", factor: 1e-3 },
  { id: "uM", symbol: "µmol/L", kind: "molar", factor: 1e-6 },
  { id: "nM", symbol: "nmol/L", kind: "molar", factor: 1e-9 },
];

export function findPartsUnit(id: string): PartsUnit {
  return PARTS_UNITS.find((unit) => unit.id === id) ?? PARTS_UNITS[0];
}

export interface PartsContext {
  /** Solution density, g/mL. */
  density: number;
  /** Molar mass of the solute, g/mol; null when unknown. */
  molarMass: number | null;
}

/** Solute concentration in g/L; null if the conversion needs a molar mass that is missing. */
export function toGramsPerLitre(
  value: number,
  unit: PartsUnit,
  context: PartsContext
): number | null {
  const base = value * unit.factor;
  if (unit.kind === "fraction") {
    return base * 1000 * context.density;
  }
  if (unit.kind === "massConc") {
    return base;
  }
  return context.molarMass === null ? null : base * context.molarMass;
}

/** The reverse of `toGramsPerLitre`. */
export function fromGramsPerLitre(
  gramsPerLitre: number,
  unit: PartsUnit,
  context: PartsContext
): number | null {
  if (unit.kind === "fraction") {
    return gramsPerLitre / (1000 * context.density) / unit.factor;
  }
  if (unit.kind === "massConc") {
    return gramsPerLitre / unit.factor;
  }
  return context.molarMass === null
    ? null
    : gramsPerLitre / context.molarMass / unit.factor;
}

export interface Equivalent {
  unit: PartsUnit;
  /** Null when the unit needs a molar mass that was not given. */
  value: number | null;
}

/** The same concentration in every unit; molar units are null without a molar mass. */
export function equivalents(
  value: number,
  fromId: string,
  context: PartsContext
): Equivalent[] | null {
  if (!(Number.isFinite(value) && value >= 0 && context.density > 0)) {
    return null;
  }
  const grams = toGramsPerLitre(value, findPartsUnit(fromId), context);
  if (grams === null) {
    return null;
  }
  return PARTS_UNITS.map((unit) => ({
    unit,
    value: fromGramsPerLitre(grams, unit, context),
  }));
}
